import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Search, FolderTree, FileCode2, ChevronRight, ChevronDown, AlertCircle,
  Bug, Loader2, Copy, RefreshCw, Network, Layers, CheckCircle2, X, ListTree,
  CircleDot, Clock, CircleCheck, CircleSlash, Sparkles,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

/**
 * MaintenanceTreePage — VOS "Support Doc cum Critical Maintenance Tree".
 *
 * Founder spec Feb 2026 (annotated tree sketch):
 *   "I want to see the entire TREE and branches of each function nested
 *   and connected, the CHAIN & FLOW. If I fix anything in MY TASK for
 *   vendor, it does not update in MY TASK of freelancer or employees.
 *   I should be able to FEED the issue and press submit — our agent
 *   should fix that. Also keep the trail of what fix we did."
 *
 * UX layout (3 columns):
 *   • LEFT     — category-grouped node tree (collapsible)
 *   • CENTER   — selected node detail + sibling list + dependency arrows
 *   • RIGHT    — Issue Inbox (with status pills) + Report-Issue modal
 *
 * Data: /api/maintenance/tree + /api/maintenance/issues
 */
const STATUS_STYLES = {
  open:        { label: 'OPEN',        cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40',  Icon: CircleDot },
  in_progress: { label: 'IN PROGRESS', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40',     Icon: Clock },
  fixed:       { label: 'FIXED',       cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', Icon: CircleCheck },
  wontfix:     { label: "WON'T FIX",   cls: 'bg-zinc-700 text-zinc-400 border-zinc-600',          Icon: CircleSlash },
};

const SEVERITY_STYLES = {
  p0: 'bg-rose-500/20 text-rose-300 border-rose-500/40',
  p1: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  p2: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  p3: 'bg-zinc-700 text-zinc-400 border-zinc-600',
};

export default function MaintenanceTreePage() {
  const { api } = useAuth();
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [nodeDetail, setNodeDetail] = useState(null);
  const [issues, setIssues] = useState([]);
  const [issueCounts, setIssueCounts] = useState({});
  const [issueFilter, setIssueFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);
  const [expandedCats, setExpandedCats] = useState(new Set(['my_tasks', 'uploads', 'approvals']));

  const loadTree = useCallback(async () => {
    setError('');
    try {
      const r = await api.get('/maintenance/tree');
      setTree(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load tree');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadIssues = useCallback(async () => {
    try {
      const r = await api.get('/maintenance/issues');
      setIssues(r.data.issues || []);
      setIssueCounts(r.data.counts || {});
    } catch (e) {
      // silent — the inbox is secondary
    }
  }, []);

  useEffect(() => { loadTree(); loadIssues(); }, [loadTree, loadIssues]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/maintenance/tree/refresh');
      await loadTree();
      toast.success('Tree rebuilt from source');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  // Group nodes by category for the left rail tree.
  const grouped = useMemo(() => {
    if (!tree?.nodes) return {};
    const g = {};
    const sq = search.trim().toLowerCase();
    for (const n of tree.nodes) {
      if (sq && !(n.label.toLowerCase().includes(sq) || n.file.toLowerCase().includes(sq))) continue;
      const cat = n.category || 'general';
      if (!g[cat]) g[cat] = [];
      g[cat].push(n);
    }
    Object.values(g).forEach(arr => arr.sort((a, b) => a.label.localeCompare(b.label)));
    return g;
  }, [tree, search]);

  // Load full node detail when selected.
  useEffect(() => {
    if (!selectedId) { setNodeDetail(null); return; }
    let cancelled = false;
    api.get(`/maintenance/tree/node/${encodeURIComponent(selectedId)}`)
      .then(r => { if (!cancelled) setNodeDetail(r.data); })
      .catch(() => { if (!cancelled) setNodeDetail(null); });
    return () => { cancelled = true; };
  }, [selectedId, issues]); // also refetch when issues change so the per-node issue list updates

  const filteredIssues = useMemo(() => {
    if (issueFilter === 'all') return issues;
    return issues.filter(i => i.status === issueFilter);
  }, [issues, issueFilter]);

  const onIssueCreated = async () => {
    setShowReportModal(false);
    await loadIssues();
    toast.success('Issue saved. AI fix prompt is generating in the background…');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading the maintenance tree…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-rose-400">
        <AlertCircle className="w-5 h-5 mr-2" /> {error}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200" data-testid="maintenance-tree-page">
      {/* Header */}
      <div className="px-6 py-3 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <ListTree className="w-4 h-4 text-zinc-950" />
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest text-violet-400 font-bold">Support Doc cum Maintenance Tree</p>
            <h1 className="text-sm text-white font-medium">
              {tree?.stats?.frontend_nodes || 0} frontend · {tree?.stats?.backend_nodes || 0} backend ·
              <span className="text-zinc-500"> {tree?.stats?.total_edges || 0} edges · {tree?.stats?.total_routes || 0} routes</span>
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-zinc-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search files / features…"
              data-testid="maintenance-search"
              className="pl-8 pr-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded text-[11px] text-zinc-200 w-64 focus:outline-none focus:border-violet-500/60"
            />
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="maintenance-refresh"
            className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded text-[11px] text-zinc-300 flex items-center gap-1.5 disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Rebuild
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[280px_1fr_360px] min-h-[calc(100vh-64px)]">

        {/* LEFT — category tree */}
        <aside className="border-r border-zinc-800 bg-zinc-950 overflow-y-auto max-h-[calc(100vh-64px)]" data-testid="maintenance-tree-left">
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([cat, nodes]) => {
            const expanded = expandedCats.has(cat);
            return (
              <div key={cat} className="border-b border-zinc-900">
                <button
                  type="button"
                  onClick={() => {
                    const next = new Set(expandedCats);
                    if (next.has(cat)) next.delete(cat); else next.add(cat);
                    setExpandedCats(next);
                  }}
                  data-testid={`maintenance-cat-${cat}`}
                  className="w-full px-3 py-2 flex items-center justify-between hover:bg-zinc-900/60"
                >
                  <span className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                    {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <FolderTree className="w-3 h-3 text-violet-400" />
                    {cat.replace(/_/g, ' ')}
                  </span>
                  <span className="text-[9px] text-zinc-600 bg-zinc-900 px-1.5 rounded font-mono">{nodes.length}</span>
                </button>
                {expanded && (
                  <div className="pb-1">
                    {nodes.map(n => (
                      <button
                        key={n.id}
                        onClick={() => setSelectedId(n.id)}
                        data-testid={`maintenance-node-${n.label}`}
                        className={`w-full px-4 py-1.5 flex items-center gap-2 text-left text-[11px] transition-colors ${
                          selectedId === n.id
                            ? 'bg-violet-500/10 text-violet-200 border-l-2 border-violet-500'
                            : 'text-zinc-400 hover:bg-zinc-900/40 hover:text-zinc-200'
                        }`}
                      >
                        <FileCode2 className="w-3 h-3 flex-shrink-0 opacity-60" />
                        <span className="truncate flex-1">{n.label}</span>
                        {n.kind === 'page' && <span className="text-[8px] uppercase px-1 rounded bg-amber-500/10 text-amber-300">page</span>}
                        {n.kind === 'backend' && <span className="text-[8px] uppercase px-1 rounded bg-emerald-500/10 text-emerald-300">api</span>}
                      </button>
                    ))}
                  </div>
                )}              </div>
            );
          })}
          {Object.keys(grouped).length === 0 && (
            <p className="text-[10px] text-zinc-600 italic px-3 py-4">No matches for "{search}".</p>
          )}
        </aside>

        {/* CENTER — node detail */}
        <main className="overflow-y-auto max-h-[calc(100vh-64px)] p-4" data-testid="maintenance-tree-center">
          {!selectedId ? (
            <EmptyState tree={tree} />
          ) : !nodeDetail ? (
            <div className="flex items-center gap-2 text-zinc-500 text-[11px] py-10 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading node details…
            </div>
          ) : (
            <NodeDetail
              api={api}
              detail={nodeDetail}
              tree={tree}
              onSelect={setSelectedId}
              onReportIssue={() => setShowReportModal(true)}
              onIssueUpdated={loadIssues}
            />
          )}
        </main>

        {/* RIGHT — issue inbox */}
        <aside className="border-l border-zinc-800 bg-zinc-950 overflow-y-auto max-h-[calc(100vh-64px)]" data-testid="maintenance-inbox">
          <div className="px-4 py-3 border-b border-zinc-800 sticky top-0 bg-zinc-950">
            <p className="text-[9px] uppercase tracking-wider text-violet-400 font-bold mb-2">Issue Inbox · Audit Trail</p>
            <div className="flex flex-wrap gap-1.5">
              <FilterPill active={issueFilter==='all'} onClick={() => setIssueFilter('all')} label={`ALL · ${issues.length}`} />
              {Object.entries(STATUS_STYLES).map(([k, v]) => (
                <FilterPill key={k} active={issueFilter===k} onClick={() => setIssueFilter(k)}
                  label={`${v.label} · ${issueCounts[k] || 0}`} cls={v.cls} />
              ))}
            </div>
          </div>
          <div className="p-2 space-y-1.5">
            {filteredIssues.length === 0 && (
              <p className="text-[10px] text-zinc-600 italic px-2 py-4 text-center">
                No issues yet. Click any node, then "Report Issue".
              </p>
            )}
            {filteredIssues.map(iss => (
              <IssueCard key={iss.id} issue={iss} api={api}
                onSelect={() => setSelectedId(iss.node_id)}
                onIssueUpdated={loadIssues} />
            ))}
          </div>
        </aside>
      </div>

      {showReportModal && nodeDetail && (
        <ReportIssueModal
          api={api}
          node={nodeDetail.node}
          onClose={() => setShowReportModal(false)}
          onCreated={onIssueCreated}
        />
      )}
    </div>
  );
}

/* ──────────────────────────── EMPTY STATE ────────────────────────── */
function EmptyState({ tree }) {
  return (
    <div className="max-w-3xl mx-auto py-12 text-center">
      <Sparkles className="w-10 h-10 text-violet-400 mx-auto mb-4" />
      <h2 className="text-lg text-white font-medium mb-1">Pick any node on the left to inspect it.</h2>
      <p className="text-[11px] text-zinc-500 leading-relaxed max-w-md mx-auto">
        Click a file to see its dependencies, siblings that must stay in sync, and the issue history.
        Then hit <span className="text-violet-300 font-bold">Report Issue</span> — GPT-5.2 drafts an Emergent-ready fix prompt you can paste into the next chat.
      </p>
      {tree?.overrides?.groups?.length > 0 && (
        <div className="mt-8 text-left">
          <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-bold mb-2">Curated Sibling Groups</p>
          <div className="space-y-2">
            {tree.overrides.groups.map(g => (
              <div key={g.id} className="px-3 py-2.5 rounded border border-zinc-800 bg-zinc-900/50">
                <p className="text-[11px] text-white font-medium">{g.label}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">{g.description}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {g.members.map(m => (
                    <span key={m} className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 font-mono">{m.split('/').pop()}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── NODE DETAIL ─────────────────────────── */
function NodeDetail({ api, detail, tree, onSelect, onReportIssue, onIssueUpdated }) {
  const node = detail.node;
  const matchingGroups = (tree?.overrides?.groups || []).filter(g =>
    g.members.some(m => m === node.file || m.endsWith('/' + node.file.split('/').pop()))
  );

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      {/* Title row */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[9px] uppercase tracking-wider text-zinc-500">{node.category} · {node.kind}</p>
          <h2 className="text-base text-white font-medium mt-0.5">{node.label}</h2>
          <p className="text-[10px] text-zinc-500 font-mono mt-0.5">{node.file}</p>
        </div>
        <button
          onClick={onReportIssue}
          data-testid="maintenance-report-issue"
          className="flex-shrink-0 px-3 py-1.5 rounded text-[11px] font-medium bg-gradient-to-r from-rose-500 to-fuchsia-500 hover:from-rose-400 hover:to-fuchsia-400 text-white flex items-center gap-1.5"
        >
          <Bug className="w-3 h-3" /> Report Issue
        </button>
      </div>

      {/* Curated sibling groups callout */}
      {matchingGroups.length > 0 && (
        <div className="px-3 py-2.5 rounded border border-violet-500/30 bg-violet-500/5 space-y-2">
          {matchingGroups.map(g => (
            <div key={g.id}>
              <p className="text-[10px] uppercase tracking-wider text-violet-300 font-bold flex items-center gap-1.5">
                <Layers className="w-3 h-3" /> Part of: {g.label}
              </p>
              <p className="text-[10px] text-zinc-400 mt-0.5">{g.description}</p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {g.members.filter(m => m !== node.file).map(m => (
                  <button key={m} onClick={() => onSelect(m)}
                    className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 hover:bg-violet-500/20 hover:text-violet-200 font-mono">
                    {m.split('/').pop()}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 3-column relationships */}
      <div className="grid grid-cols-3 gap-3">
        <RelationColumn label="Imports" subtitle="This file depends on" icon={ChevronRight}
          nodes={detail.imports} onSelect={onSelect} />
        <RelationColumn label="Imported By" subtitle="Other files depend on this" icon={ChevronRight}
          nodes={detail.imported_by} onSelect={onSelect} colorClass="text-cyan-400" />
        <RelationColumn label="Siblings" subtitle="Auto-detected via shared importer" icon={Network}
          nodes={detail.siblings} onSelect={onSelect} colorClass="text-fuchsia-400" />
      </div>

      {/* Backend endpoints */}
      {node.backend_endpoints && node.backend_endpoints.length > 0 && (
        <div className="px-3 py-2.5 rounded border border-zinc-800 bg-zinc-900/50">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">Backend Endpoints Hit</p>
          <div className="flex flex-wrap gap-1">
            {node.backend_endpoints.map(ep => (
              <span key={ep} className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 font-mono">
                {ep}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Routes declared (backend modules) */}
      {node.routes && node.routes.length > 0 && (
        <div className="px-3 py-2.5 rounded border border-zinc-800 bg-zinc-900/50">
          <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">
            Routes Declared In This File ({node.routes.length})
          </p>
          <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
            {node.routes.map((r, i) => (
              <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/30 font-mono">
                {r.method} {r.path}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Issues for this node */}
      <div className="px-3 py-2.5 rounded border border-zinc-800 bg-zinc-900/50">
        <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold mb-2">
          Issues Tracked Against This Node ({(detail.issues || []).length})
        </p>
        {(detail.issues || []).length === 0 ? (
          <p className="text-[10px] text-zinc-600 italic">No issues yet.</p>
        ) : (
          <div className="space-y-1.5">
            {detail.issues.map(iss => (
              <IssueCard key={iss.id} issue={iss} api={api} compact onIssueUpdated={onIssueUpdated} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RelationColumn({ label, subtitle, nodes, onSelect, colorClass = 'text-zinc-400', icon: Icon }) {
  return (
    <div className="px-3 py-2.5 rounded border border-zinc-800 bg-zinc-900/50">
      <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold flex items-center gap-1">
        <Icon className={`w-3 h-3 ${colorClass}`} /> {label}
      </p>
      <p className="text-[9px] text-zinc-600 italic mt-0.5 mb-2">{subtitle}</p>
      {nodes.length === 0 ? (
        <p className="text-[10px] text-zinc-600 italic">(none)</p>
      ) : (
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {nodes.slice(0, 30).map(n => (
            <button key={n.id} onClick={() => onSelect(n.id)}
              className="w-full text-left text-[10px] text-zinc-300 hover:text-violet-200 hover:bg-violet-500/5 px-1.5 py-1 rounded truncate font-mono">
              {n.label}
            </button>
          ))}
          {nodes.length > 30 && <p className="text-[9px] text-zinc-600 px-1.5">…and {nodes.length - 30} more</p>}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── ISSUE CARD ──────────────────────────── */
function IssueCard({ issue, api, onSelect, compact, onIssueUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [fixNote, setFixNote] = useState('');
  const [pendingStatus, setPendingStatus] = useState('');
  const ss = STATUS_STYLES[issue.status] || STATUS_STYLES.open;
  const Sev = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.p2;

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const body = { status: newStatus };
      if (fixNote.trim()) body.fix_note = fixNote.trim();
      await api.patch(`/maintenance/issues/${issue.id}`, body);
      setFixNote('');
      setPendingStatus('');
      if (onIssueUpdated) onIssueUpdated();
      toast.success(`Marked ${STATUS_STYLES[newStatus]?.label || newStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setUpdating(false);
    }
  };

  const copyPrompt = () => {
    if (!issue.fix_prompt) return;
    navigator.clipboard.writeText(issue.fix_prompt).catch(() => {});
    toast.success('Emergent fix-prompt copied to clipboard');
  };

  return (
    <div className={`rounded border ${expanded ? 'border-violet-500/40 bg-zinc-900' : 'border-zinc-800 bg-zinc-900/50'} transition-colors`} data-testid={`maintenance-issue-${issue.id}`}>
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full px-2.5 py-2 text-left flex items-start gap-2 hover:bg-zinc-800/40"
      >
        <ss.Icon className="w-3 h-3 mt-0.5 flex-shrink-0 text-zinc-500" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-zinc-200 font-medium truncate">{issue.title}</p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span className={`text-[8px] uppercase px-1 rounded border font-bold ${ss.cls}`}>{ss.label}</span>
            <span className={`text-[8px] uppercase px-1 rounded border font-bold ${Sev}`}>{issue.severity?.toUpperCase()}</span>
            {!compact && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onSelect && onSelect(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onSelect && onSelect(); } }}
                className="text-[9px] text-zinc-500 hover:text-violet-200 font-mono truncate cursor-pointer"
                title={issue.node_file}
              >
                {issue.node_label}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className={`w-3 h-3 text-zinc-600 mt-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="px-2.5 pb-2 space-y-2 border-t border-zinc-800/60">
          <p className="text-[10px] text-zinc-400 whitespace-pre-wrap leading-relaxed pt-2">{issue.description}</p>

          {/* AI-generated fix prompt */}
          <div className="rounded bg-zinc-950 border border-violet-500/20 p-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <p className="text-[8px] uppercase tracking-wider text-violet-400 font-bold flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5" /> Emergent-ready Fix Prompt
              </p>
              <button
                type="button"
                onClick={copyPrompt}
                disabled={!issue.fix_prompt || issue.fix_prompt_generating}
                className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-200 hover:bg-violet-500/30 flex items-center gap-1 disabled:opacity-40"
              >
                <Copy className="w-2.5 h-2.5" /> Copy
              </button>
            </div>
            {issue.fix_prompt_generating ? (
              <p className="text-[10px] text-zinc-500 italic flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> GPT-5.2 is drafting the fix brief…
              </p>
            ) : issue.fix_prompt ? (
              <pre className="text-[9px] text-zinc-300 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto leading-snug">{issue.fix_prompt}</pre>
            ) : (
              <p className="text-[10px] text-zinc-500 italic">No prompt generated yet.</p>
            )}
          </div>

          {/* Fix trail */}
          {(issue.fix_trail || []).length > 0 && (
            <div>
              <p className="text-[8px] uppercase tracking-wider text-zinc-500 font-bold mb-1">Fix Trail</p>
              <div className="space-y-1">
                {issue.fix_trail.map((t, i) => (
                  <div key={i} className="text-[9px] text-zinc-400 border-l-2 border-emerald-500/40 pl-1.5">
                    <p className="text-zinc-200">{t.note}</p>
                    <p className="text-zinc-600">— {t.by_name} · {new Date(t.at).toLocaleString()}{t.new_status ? ` · → ${t.new_status}` : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status actions */}
          {pendingStatus ? (
            <div className="space-y-1.5">
              <textarea
                value={fixNote}
                onChange={(e) => setFixNote(e.target.value)}
                placeholder="What did you change to fix this? (audit trail)"
                rows={2}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-[10px] text-zinc-200 resize-none focus:outline-none focus:border-violet-500/60"
              />
              <div className="flex gap-1.5">
                <button
                  onClick={() => updateStatus(pendingStatus)} disabled={updating}
                  className="flex-1 px-2 py-1 text-[10px] font-medium bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 rounded border border-emerald-500/30 disabled:opacity-40"
                >
                  {updating ? '...' : `Save → ${STATUS_STYLES[pendingStatus]?.label}`}
                </button>
                <button onClick={() => { setPendingStatus(''); setFixNote(''); }}
                  className="px-2 py-1 text-[10px] text-zinc-400 hover:text-white rounded">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-1 flex-wrap">
              {Object.entries(STATUS_STYLES)
                .filter(([k]) => k !== issue.status)
                .map(([k, v]) => (
                  <button key={k}
                    onClick={() => setPendingStatus(k)}
                    className={`text-[9px] px-1.5 py-0.5 rounded border ${v.cls} hover:opacity-100 opacity-70`}>
                    → {v.label}
                  </button>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterPill({ active, label, onClick, cls }) {
  return (
    <button
      onClick={onClick}
      className={`text-[9px] px-1.5 py-0.5 rounded border font-bold ${
        active ? 'bg-violet-500/20 text-violet-200 border-violet-500/40' : (cls || 'bg-zinc-900 text-zinc-400 border-zinc-800')
      }`}
    >
      {label}
    </button>
  );
}

/* ──────────────────────────── REPORT MODAL ────────────────────────── */
function ReportIssueModal({ api, node, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('p2');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !description.trim()) return;
    setBusy(true);
    try {
      await api.post('/maintenance/issues', {
        node_id: node.id, title: title.trim(), description: description.trim(), severity,
      });
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save issue');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="maintenance-report-modal">
      <div className="bg-zinc-950 border border-rose-500/40 rounded-lg shadow-2xl w-[560px] max-w-[92vw] max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-rose-500/20 flex items-center justify-between bg-gradient-to-r from-rose-950/40 to-zinc-950">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-rose-400 font-bold">Report Issue</p>
            <h2 className="text-sm text-white font-medium mt-0.5">{node.label} <span className="text-zinc-500 text-[10px] font-mono">· {node.file}</span></h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1 rounded hover:bg-zinc-800"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1">
          <section>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1 block">Title <span className="text-rose-400">*</span></label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              data-testid="maintenance-report-title"
              placeholder="One-line summary (e.g. 'Upload silently fails for vendor MyTasks')"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-[11px] text-white focus:outline-none focus:border-rose-500/60" />
          </section>
          <section>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1 block">Severity</label>
            <div className="flex gap-1.5">
              {['p0', 'p1', 'p2', 'p3'].map(s => (
                <button key={s}
                  onClick={() => setSeverity(s)}
                  data-testid={`maintenance-report-sev-${s}`}
                  className={`text-[10px] px-2.5 py-1 rounded border font-bold uppercase ${severity === s ? SEVERITY_STYLES[s] : 'bg-zinc-900 text-zinc-500 border-zinc-800'}`}>
                  {s}
                </button>
              ))}
            </div>
          </section>
          <section>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1 block">What's broken / what needs to change? <span className="text-rose-400">*</span></label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="maintenance-report-description"
              rows={6}
              placeholder="Steps to reproduce, expected vs. actual behaviour, screenshots references, related sibling files…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded px-2.5 py-1.5 text-[11px] text-zinc-200 resize-none focus:outline-none focus:border-rose-500/60" />
            <p className="text-[9px] text-zinc-600 italic mt-1">
              GPT-5.2 will draft an Emergent-ready fix prompt from this. The more detail, the better the prompt.
            </p>
          </section>
        </div>
        <div className="px-4 py-2.5 border-t border-zinc-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-[11px] text-zinc-300 hover:text-white rounded">Cancel</button>
          <button onClick={submit} disabled={busy || !title.trim() || !description.trim()}
            data-testid="maintenance-report-submit"
            className="px-4 py-1.5 text-[11px] font-medium rounded bg-gradient-to-r from-rose-500 to-fuchsia-500 hover:from-rose-400 hover:to-fuchsia-400 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
            Save Issue
          </button>
        </div>
      </div>
    </div>
  );
}
